import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import ClickOutside from 'react-click-outside'
import qs from 'query-string'
import styles from './search-nav-button.css'
import { useGetSearchConfig } from '../../../modules/search/hooks/use-get-search-config'
import { setSearchIsClicked } from '../../../actions'
import { searchGa } from '../../../lib/analytics/ga'
import { customDimensions } from '../../../lib/analytics/custom-dimensions'
import { getSelectedProfileIdFromSession } from '../../../lib/account'
import searchCross from '../../../../images/neon/close-filled.svg'

function SearchNavButton({ isAuthenticated }) {
  const inputRef = useRef(null)
  const location = useLocation()
  const { search, ...restQueryParams } = qs.parse(location.search)
  const searchQueryParam = search || ''
  const history = useHistory()
  const dispatch = useDispatch()
  const isClicked = useSelector(state => state.navigation.searchIsClicked)
  const profileId = useSelector(state => getSelectedProfileIdFromSession(state.session))
  const [query, setQuery] = useState(() => qs.parse(location.search).search || '')
  const timeoutRef = useRef(null)
  const { debounceMilliSec } = useGetSearchConfig()
  const searchNavButtonClass = isClicked ? styles.searchWrapperFocus : styles.searchWrapper

  const handleInputValueChange = event => {
    // Block password manager and other synthetic autofill events.
    // isTrusted is true for real user keyboard/input events,
    // while synthetic password manager events are not trusted.
    if (!event.isTrusted) return
    setQuery(event.target.value)
  }

  /**
   * when {@link query} is changed, update URL with search param (wait for debounceMilliSec before update)
   */
  useEffect(() => {
    if (query === searchQueryParam) return
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      if (!query) {
        history.push({ pathname: location.pathname, search: '' })
      } else {
        history.push({
          pathname: location.pathname,
          search: qs.stringify({ search: mergeAllWhitespace(query) })
        })
      }
      timeoutRef.current = null
    }, debounceMilliSec)
  }, [query])

  /**
   * when search in URL search param is changed, update state {@link query}
   */
  useEffect(() => {
    // skip when there are other search params (e.g. modal)
    if (Object.keys(restQueryParams).length > 0) return

    if (!searchQueryParam) {
      setQuery('')
    } else {
      setQuery(mergeAllWhitespace(searchQueryParam))
    }
  }, [searchQueryParam])

  // when isClicked is changed, focus/unfocus the search input
  useEffect(() => {
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur()
    }
    if (isClicked) {
      inputRef.current?.focus()
    } else {
      inputRef.current?.blur()
    }
  }, [isClicked])

  /**
   * when search button is clicked, dispatch event to change redux state {@link isClicked}
   */
  const handleInputClicked = event => {
    event.preventDefault()
    searchGa({ [customDimensions.ProfileId]: profileId })
    dispatch(setSearchIsClicked(true))
  }

  const handleOnClickOutside = () => {
    if (isClicked) {
      dispatch(setSearchIsClicked(false))
    }
  }

  const handleClearClicked = () => {
    setQuery('')
    inputRef.current.focus()
  }

  const isLoggedOutOnKidsPage =
    !isAuthenticated && location.pathname === '/kids'
  if (isLoggedOutOnKidsPage) return null

  return (
    <ClickOutside
      className={classNames({
        [searchNavButtonClass]: true,
        [styles.hide]: !isAuthenticated && !isClicked
      })}
      onClickOutside={handleOnClickOutside}
    >
      <div className={styles.searchInner} data-e2e="nav-search">
        <input
          ref={inputRef}
          name="search"
          value={query}
          onChange={handleInputValueChange}
          onClick={handleInputClicked}
          id="navSearch"
          type="search"
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore
          data-dashlane-ignore="true"
          data-bwignore="true"
          className={styles.searchInput}
          placeholder="Search by title, people or genre"
          data-e2e="nav-search-input-placeholder"
        />
        <label htmlFor="navSearch" className={styles.searchLabel}>
          Search
        </label>
        {query && isClicked && (
          <img
            className={styles.searchCross}
            src={searchCross}
            onClick={handleClearClicked}
          />
        )}
      </div>
    </ClickOutside>
  )
}

SearchNavButton.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired
}

export default SearchNavButton

/**
 * if the whole string is white spaces, merge to single white space; otherwise no change
 */
const mergeAllWhitespace = str => str.replace(/^\s+$/, ' ')
